import { getLocale, LOCALES, setLocale, t, type Locale } from './i18n';

/**
 * The language switcher, in two shapes for the two places it appears.
 *
 * Both render a plain set of buttons rather than a `<select>`: with exactly
 * two locales a dropdown costs a tap to discover what is even in it, and the
 * compact variant has to sit in the landing header next to the Play button on
 * a 320px screen.
 *
 * Neither variant re-renders anything itself — the caller owns that, because
 * "what has to be re-rendered when the language changes" is a question only
 * the mounting screen can answer. See `onChange`.
 */

/** Markup only. Call `mountLanguageSwitcher` on the container to make it live. */
export function languageSwitcherHtml(variant: 'compact' | 'full' = 'full'): string {
  const current = getLocale();

  if (variant === 'compact') {
    // Landing header: uppercase language codes, sized to the 44px tap target
    // CLAUDE.md requires, narrow enough to sit beside the Play button.
    return `
      <div class="flex items-center rounded border border-border overflow-hidden" role="group" aria-label="${t('lang.switch')}" data-language-switcher>
        ${LOCALES.map(
          (loc) => `
          <button type="button" data-action="set-locale" data-locale="${loc}"
            aria-pressed="${loc === current}"
            title="${t(`lang.name.${loc}` as 'lang.name.en')}"
            class="min-h-11 px-2.5 text-[11px] font-heading uppercase tracking-wide transition-colors duration-[120ms] touch-manipulation ${
              loc === current ? 'bg-gold text-bg' : 'text-text-dim hover:text-text'
            }">${loc}</button>`,
        ).join('')}
      </div>
    `;
  }

  // Settings: full language names, matching the row layout the Audio section uses.
  return `
    <div class="flex items-center justify-between gap-3" data-language-switcher>
      <span class="min-w-0">
        <span class="block text-sm text-text">${t('settings.language')}</span>
        <span class="block text-[10px] text-text-dim leading-relaxed">${t('settings.languageHint')}</span>
      </span>
      <div class="shrink-0 flex gap-1.5" role="group" aria-label="${t('lang.switch')}">
        ${LOCALES.map(
          (loc) => `
          <button type="button" data-action="set-locale" data-locale="${loc}"
            aria-pressed="${loc === current}"
            class="min-h-11 px-3 rounded text-xs font-heading transition-colors duration-[120ms] touch-manipulation ${
              loc === current
                ? 'bg-gold/20 text-gold border border-gold/40'
                : 'bg-surface-2 text-text-dim border border-border hover:text-text hover:border-gold/30'
            }">${t(`lang.name.${loc}` as 'lang.name.en')}</button>`,
        ).join('')}
      </div>
    </div>
  `;
}

/**
 * Delegates clicks on any switcher inside `root`. `onChange` fires only when
 * the locale actually changed, so a caller can re-render unconditionally
 * without guarding against a no-op re-mount.
 *
 * Returns a teardown, same convention as the rest of engine/.
 */
export function mountLanguageSwitcher(root: HTMLElement, onChange: (locale: Locale) => void): () => void {
  function onClick(e: Event): void {
    const el = (e.target as HTMLElement).closest('[data-action="set-locale"]') as HTMLElement | null;
    if (!el) return;
    const next = el.dataset.locale as Locale | undefined;
    if (!next || next === getLocale()) return;
    setLocale(next);
    onChange(next);
  }
  root.addEventListener('click', onClick);
  return () => root.removeEventListener('click', onClick);
}
